import { AudioPlayer } from '@components/AudioPlayer';
import { ExternalFileDownloadLink } from '@components/ExternalFileDownloadLink';
import { FileDownloadButton } from '@components/FileDownloadButton';
import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { AutoCompleteSelect } from '@components/core-ui/form-elements/AutocompleteSelect';
import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { IconButtonRegular } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { ToolbarActionButton } from '@components/core-ui/form-elements/buttons/Button/variations/ToolbarActionButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Header } from '@components/Header';
import ASOneIcon from '@components/icons/ASOne.svg';
import DesktopIcon from '@components/icons/Desktop.svg';
import DoneIcon from '@components/icons/Done.svg';
import DownloadJob from '@components/icons/DownloadJob.svg';
import DownloadZip from '@components/icons/DownloadZip.svg';
import DiagonalArrowIcon from '@components/icons/DiagonalArrow.svg';
import LoadingIcon from '@components/icons/Loading.svg';
import MagicWand from '@components/icons/MagicWand.svg';
import PencilIcon from '@components/icons/Pencil.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import Share from '@components/icons/ShareIcon.svg';
import { Popover } from '@components/core-ui/Popover';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { TagMuted } from '@components/core-ui/Tag/variations/TagMuted';
import { KeyTermsList } from '@components/KeyTermsList';
import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { canChooseCloudLayout } from '@helpers/canChooseCloudLayout';
import { hasVersionsBeyondLayoutDraft } from '@helpers/hasVersionsBeyondLayoutDraft';
import { type SpeakerLabelOverride } from '@helpers/buildSpeakerLabelOverrides';
import { buildJobMaterialFileName } from '@helpers/buildJobMaterialFileName';
import { formatIsoDateToAmerican } from '@helpers/formatIsoDateToAmerican';
import { hasSpecialCharacters } from '@helpers/hasSpecialCharacters';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { getTranscriptUnlockTypeName } from '@helpers/getTranscriptUnlockTypeName';
import { openCloudLayoutRegenerationFlow } from '@helpers/openCloudLayoutRegenerationFlow';
import { shouldShowSpeakerLabelOverrides } from '@helpers/shouldShowSpeakerLabelOverrides';
import { getLinkPrefix, handleDownloadError, handleOpenDesktop } from '@helpers/jobLinkHelpers';
import { downloadFromUrl } from '@helpers/downloadFromUrl';
import { downloadViaBrowser } from '@helpers/downloadViaBrowser';
import { isAssemblyKeytermsEnabled } from '@helpers/featureFlags/isAssemblyKeytermsEnabled';
import { jobCanBeDownloaded } from '@helpers/jobStatus/jobCanBeDownloaded';
import { jobCanBeOpenedInASD } from '@helpers/jobStatus/jobCanBeOpenedInASD';
import { jobCanBeReassigned } from '@helpers/jobStatus/jobCanBeReassigned';
import { jobCanBeShared } from '@helpers/jobStatus/jobCanBeShared';
import { jobIsScheduled } from '@helpers/jobStatus/jobIsScheduled';
import { AttachBackupButton } from '@pages/User/JobDetailsPage/components/AttachBackupButton';
import { AttorneysBlock } from '@pages/User/JobDetailsPage/components/AttorneysBlock';
import {
  showClaimButton,
  showMarkAsCompleteButton,
  showMarkAsEditedButton,
  showMarkAsProofreadButton,
} from '@pages/User/RepositoryPage/components/JobsTable/JobStatusActionsButton';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { Restricted } from '@providers/PermissionProvider/Restricted';
import { useAPI } from '@providers/useAPI';
import { useWebsocketContext } from '@providers/WebsocketProvider';
import { routes } from '@routes';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  JobDtoAttachment,
  JobAttachmentResponseDto,
  JobResponseDto,
  JobsService,
  JobSubscriptionsService,
  MediaService,
  PayService,
} from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { cancelAndInvalidateQueries } from 'app/queries/cancelAndInvalidateQueries';
import { createFetchJobAttachmentQueryFn } from 'app/queries/jobs/createFetchJobAttachmentQueryFn';
import { createFetchJobQueryFn } from 'app/queries/jobs/createFetchJobQueryFn';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { createFetchAvailableLayoutsQueryFn } from 'app/queries/layouts/createFetchAvailableLayoutsQueryFn';
import { layoutsKeys } from 'app/queries/layouts/layoutsKeys';
import { EPermission } from 'app/types/enums/EPermission';
import { JOB_STATUSES_ENUM } from 'app/types/enums/jobStatuses';
import { EErrorMessages } from 'app/types/errorMessages';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import sanitize from 'sanitize-filename';
import { createFetchPartialUnlockedFilesQueryFn } from './api/fetchPartialUnlockedFiles';
import { createFetchPlaybackAudioUrlQueryFn } from './api/fetchPlaybackAudioUrl';
import { CommentForm } from './components/CommentForm';
import { JobStatusActionsButton } from './components/JobStatusActionsButton';
import { CommentsList } from './components/CommentsList';
import MetricsList from './components/MetricsList';
import { UploadAttachmentsBlock } from './components/UploadAttachmentsBlock';
import EyeIcon from '@components/icons/FormEyeOpen.svg';
import EyeClosedIcon from '@components/icons/FormEyeClosed.svg';
import { AssigneeBlockContent } from './styles/AssigneeBlockContent';
import { AssigneeTableWrapper, CheckoutEventsTableWrapper } from './styles/AssigneeTableWrapper';
import { CellContent } from './styles/CellContent';
import { Divider } from './styles/Divider';
import { DownloadZipLink } from './styles/DownloadZipLink';
import { FlexBlockContent } from './styles/FlexBlockContent';
import { FlexNotes } from './styles/FlexNotes';
import { FlexTitle } from './styles/FlexTitle';
import { FlexTitleText } from './styles/FlexTitleText';
import { InfoBlock } from './styles/InfoBlock';
import { InfoBlockContent } from './styles/InfoBlockContent';
import { InfoBlockFullSize } from './styles/InfoBlockFullSize';
import { InfoBlockTitle } from './styles/InfoBlockTitle';
import { InfoBlockWrapper } from './styles/InfoBlockWrapper';
import { JobRightRow } from './styles/JobRightRow';
import { JobSection } from './styles/JobSection';
import { JobSectionLeft } from './styles/JobSectionLeft';
import { JobSectionRight } from './styles/JobSectionRight';
import { NoAccessContent } from './styles/NoAccessContent';
import { NoAccessTitle } from './styles/NoAccessTitle';
import { NoAccessWrapper } from './styles/NoAccessWrapper';
import { Input } from '@components/core-ui/form-elements/Input';
import { StyledButtonIconLink } from './styles/StyledButtonIconLink';
import { StyledDesktopButtonIconLink } from './styles/StyledDesktopButtonIconLink';
import { StyledIconLink } from './styles/StyledIconLink';
import { StyledLink } from './styles/StyledLink';
import { Tags } from './styles/Tags';
import { TopRightSection } from './styles/TopRightSection';
import { Wrapper } from './styles/Wrapper';

type TJobAttachmentWithNoticeData = JobDtoAttachment & {
  path: string;
  fileName: string;
  fileSize?: number;
  id?: string;
  attachmentType?: 'REPORTER_NOTES' | 'GENERAL' | 'NOTICE';
};

const isPdfAttachment = (attachment: TJobAttachmentWithNoticeData) => {
  return /\.pdf$/i.test(attachment.fileName || attachment.path);
};

const NOTICE_EXTRACTION_POLL_INTERVAL_MS = 5000;

const isNoticePdfAttachment = (attachment: TJobAttachmentWithNoticeData) => {
  return attachment.attachmentType === 'NOTICE' && isPdfAttachment(attachment) && !!attachment.id;
};

type TWatchSettings = {
  status: boolean;
  assignee: boolean;
  comments: boolean;
};

export const JobDetailsPage = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const params = useParams() as { id: string; orgSlug: string; wsSlug: string };
  const jobId = params?.id ?? '';
  const wsSlug = params?.wsSlug ?? '';
  const { me, workspace, organization } = useAuthContext();
  const { openModal, closeModal } = useModalContext();
  const { updatedJob, jobAccessLostId, offline: websocketsOffline } = useWebsocketContext();
  const navigate = useNavigate();

  const workspaceId = workspace?.id ?? '';
  const organizationId = organization?.id ?? '';

  const getWorkspaceBySlug = () => {
    return (organization?.workspaces || []).find((ws) => ws.slug === wsSlug);
  };
  const isWorkspaceAccessible = wsSlug === workspace?.slug || !!getWorkspaceBySlug();

  const jobQueryKey = jobsKeys.workspaceDetail(jobId, workspaceId);
  const jobQuery = useQuery({
    queryKey: jobQueryKey,
    queryFn: createFetchJobQueryFn(call),
    enabled: jobId !== '' && workspaceId !== '' && isWorkspaceAccessible,
  });
  const job = jobQuery.data;

  const partialUnlockedFilesQuery = useQuery({
    queryKey: jobsKeys.partialUnlockedFilesByJob(jobId),
    queryFn: createFetchPartialUnlockedFilesQueryFn(call),
    enabled: jobId !== '',
  });
  const outputUrl = partialUnlockedFilesQuery.data?.downloadOutputUrl ?? '';
  const summaryUrl = partialUnlockedFilesQuery.data?.downloadSummaryUrl ?? '';
  const showSummary =
    partialUnlockedFilesQuery.isPending || (!partialUnlockedFilesQuery.isError && !!outputUrl && !!summaryUrl);

  const playbackAudioFilename = job?.playbackAudioFilename ?? '';
  const playbackAudioQuery = useQuery({
    queryKey: jobsKeys.playbackAudioUrl(job?.id ?? '', playbackAudioFilename),
    queryFn: createFetchPlaybackAudioUrlQueryFn(call),
    enabled: !!job?.id && playbackAudioFilename !== '',
    staleTime: Infinity,
    gcTime: 0,
  });
  const playbackAudioUrl = playbackAudioQuery.data?.signedUrl || null;
  const playbackAudioLoading = playbackAudioQuery.isLoading;

  const layoutsQuery = useQuery({
    queryKey: layoutsKeys.availableByOrganization(organizationId),
    queryFn: createFetchAvailableLayoutsQueryFn(call),
    enabled: organizationId !== '' && !!job,
  });
  const layoutsList = useMemo(
    () =>
      (layoutsQuery.data ?? []).map((layout: { id: string; name: string }) => ({
        label: layout.name,
        value: layout.id,
      })),
    [layoutsQuery.data],
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement | EventTarget>(null);
  const [editTitle, setEditTitle] = useState(false);
  const [jobName, setJobName] = useState('');
  const [errorJobNameMessage, setErrorJobNameMessage] = useState('');

  const meWatcher = job?.watchers?.find((w) => w.userId === me.id);
  const [watchStatus, setWatchStatus] = useState(false);
  const [watchAssignee, setWatchAssignee] = useState(false);
  const [watchComments, setWatchComments] = useState(false);

  const [layoutId, setLayoutId] = useState<string>('');

  const reload = async () => {
    await Promise.all([
      cancelAndInvalidateQueries(queryClient, jobsKeys.detail(jobId)),
      queryClient.invalidateQueries({ queryKey: jobsKeys.listsByWorkspace(workspaceId) }),
    ]);
    setEditTitle(false);
  };

  const applyWatchSettings = (settings: TWatchSettings) => {
    setWatchStatus(settings.status);
    setWatchAssignee(settings.assignee);
    setWatchComments(settings.comments);
  };

  const watchMutation = useMutation({
    mutationFn: async (settings: TWatchSettings) => {
      if (settings.status || settings.assignee || settings.comments) {
        await call(() => JobSubscriptionsService.upsert({ jobId, requestBody: settings }));
        return;
      }
      await call(() => JobSubscriptionsService.delete({ jobId }));
    },
    onSuccess: () => reload(),
    onError: (error) => {
      notifyApiCallError(error);
      applyWatchSettings({
        status: meWatcher?.status || false,
        assignee: meWatcher?.assignee || false,
        comments: meWatcher?.comments || false,
      });
    },
  });

  const updateWatchSettings = (settings: TWatchSettings) => {
    applyWatchSettings(settings);
    if (!settings.status && !settings.assignee && !settings.comments) {
      setAnchorEl(null);
    }
    watchMutation.mutate(settings);
  };

  const saveTitleMutation = useMutation({
    mutationFn: (name: string) =>
      call(() => JobsService.updateTitle({ jobId, wsid: workspaceId, requestBody: { name } })),
    onSuccess: () => reload(),
    onError: notifyApiCallError,
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: (path: string) => call(() => JobsService.deleteAttachment({ oid: organizationId, jobId, file: path })),
    onSuccess: () => {
      toast.success('Attachment deleted');
      return reload();
    },
    onError: notifyApiCallError,
  });

  const enhanceMutation = useMutation({
    mutationFn: () => call(() => PayService.enhance({ jobId }), { onError: (message) => toast.error(message) }),
    onSuccess: () => reload(),
  });

  const caseZipMutation = useMutation({
    mutationFn: () => call(() => JobsService.getWsJobRepoZipById({ wsid: workspaceId, jobId })),
    onSuccess: (response) => {
      if (!response) {
        handleDownloadError(new Error('No case ZIP URL'));
        return;
      }
      window.open(response, '_blank', 'noopener,noreferrer');
    },
    onError: handleDownloadError,
  });

  const isBusy =
    jobQuery.isFetching ||
    watchMutation.isPending ||
    saveTitleMutation.isPending ||
    deleteAttachmentMutation.isPending ||
    enhanceMutation.isPending ||
    caseZipMutation.isPending;

  const regenerateWithLayoutMutation = useMutation({
    mutationFn: ({
      jobId: regenerateJobId,
      workspaceId,
      speakerLabelOverrides,
    }: {
      jobId: string;
      workspaceId: string;
      speakerLabelOverrides?: SpeakerLabelOverride[];
    }) =>
      call(
        () =>
          JobsService.regenerateWithNewLayout({
            jobId: regenerateJobId,
            wsid: workspaceId,
            requestBody: { layoutId, ...(speakerLabelOverrides ? { speakerLabelOverrides } : {}) },
          }),
        { onError: (message) => toast.error(message) },
      ),
    onSuccess: () => {
      toast.success('Regeneration started');
      return reload();
    },
  });

  const updateJobAttachment = useCallback(
    (attachment: JobAttachmentResponseDto) => {
      const nextAttachment: JobDtoAttachment = {
        id: attachment.id,
        fileName: attachment.filename,
        path: attachment.filename,
        fileSize: attachment.fileSize,
        extracted: attachment.extracted,
        attachmentType: attachment.attachmentType,
      };

      queryClient.setQueryData<JobResponseDto>(jobsKeys.workspaceDetail(jobId, workspaceId), (currentJob) => {
        if (!currentJob) {
          return currentJob;
        }

        return {
          ...currentJob,
          attachments: currentJob.attachments.map((item) =>
            item.id === attachment.id || item.path === attachment.filename || item.fileName === attachment.filename
              ? { ...item, ...nextAttachment }
              : item,
          ),
        };
      });
    },
    [queryClient, jobId, workspaceId],
  );

  const [paymentIntent, setPaymentIntent] = useState<{
    url: string;
    id: string;
  }>({
    url: '',
    id: '',
  });

  useEffect(() => {
    applyWatchSettings({
      status: meWatcher?.status || false,
      assignee: meWatcher?.assignee || false,
      comments: meWatcher?.comments || false,
    });
  }, [meWatcher?.status, meWatcher?.assignee, meWatcher?.comments]);

  useEffect(() => {
    setLayoutId(job?.layoutId || '');
  }, [job?.layoutId]);

  const wasFetchingJobRef = useRef(false);
  useEffect(() => {
    if (jobQuery.isFetching) {
      wasFetchingJobRef.current = true;
      return;
    }
    if (!wasFetchingJobRef.current) {
      return;
    }
    wasFetchingJobRef.current = false;
    if (jobQuery.isSuccess && jobQuery.data?.asOneLocked) {
      navigate(routes.jobsList.make(organization?.slug ?? '', wsSlug));
    }
  }, [jobQuery.isFetching, jobQuery.isSuccess, jobQuery.data?.asOneLocked, navigate, organization?.slug, wsSlug]);

  const isNoticeExtractorEnabled = organization?.featureFlags?.NOTICE_EXTRACTOR_ENABLED === true;
  const pendingNoticeAttachmentIds =
    isNoticeExtractorEnabled && organizationId !== '' && workspaceId !== '' && job?.id
      ? ((job.attachments ?? []) as TJobAttachmentWithNoticeData[])
          .filter((attachment) => isNoticePdfAttachment(attachment) && !attachment.extracted)
          .map((attachment) => attachment.id as string)
      : [];
  const noticeAttachmentQueries = useQueries({
    queries: pendingNoticeAttachmentIds.map((attachmentId) => ({
      queryKey: jobsKeys.attachment(jobId, workspaceId, attachmentId),
      queryFn: createFetchJobAttachmentQueryFn(call),
      refetchInterval: (query: { state: { data?: JobAttachmentResponseDto } }) =>
        query.state.data?.extracted ? false : NOTICE_EXTRACTION_POLL_INTERVAL_MS,
      refetchIntervalInBackground: true,
    })),
  });
  const extractedNoticeAttachmentIds = noticeAttachmentQueries
    .flatMap((query) => (query.data?.extracted ? [query.data.id] : []))
    .join(',');

  useEffect(() => {
    if (!extractedNoticeAttachmentIds) {
      return;
    }
    extractedNoticeAttachmentIds.split(',').forEach((attachmentId) => {
      const attachment = queryClient.getQueryData<JobAttachmentResponseDto>(
        jobsKeys.attachment(jobId, workspaceId, attachmentId),
      );
      if (attachment) {
        updateJobAttachment(attachment);
      }
    });
  }, [extractedNoticeAttachmentIds, jobId, workspaceId, queryClient, updateJobAttachment]);

  const canEditJob = organization?.permissions?.editJobs;
  const canSubmitJob = organization?.permissions?.submitJobs;
  const showDownloads =
    (me.id === job?.assigneeId ||
      ['all', 'assignedAndUnassigned'].includes(String(organization?.permissions?.viewJobs))) &&
    job &&
    jobCanBeDownloaded(job.status);

  const showSourceFiles = job?.sourceFiles?.length && job.status && !job.asOneLocked && jobCanBeDownloaded(job.status);
  const showBackupFiles = job?.backupFiles?.length && canSubmitJob;
  const showMaterials = !!job?.notes || canEditJob || showDownloads || !!job?.repository?.slug;
  const canUploadMaterials = !!job?.status && jobCanBeDownloaded(job.status);
  const keyTerms = (job?.keyTerms ?? []).filter((keyTerm) => !!keyTerm);
  const showKeyTerms = isAssemblyKeytermsEnabled(organization?.featureFlags) && keyTerms.length > 0;
  const showSchedule = !!job?.meetingType;
  const showVersions = !!job?.checkoutEvents?.length;
  const canBeOpenedInASD = job?.status && jobCanBeOpenedInASD(job?.status ?? '');
  const isCloudLayoutChoiceAvailable = canChooseCloudLayout(job?.status, job?.versions);
  const canRegenerateWithLayout =
    isCloudLayoutChoiceAvailable && Boolean(canEditJob) && (job?.layoutId || '') !== layoutId;

  const canTriggerEnhanced =
    (canEditJob || canSubmitJob) &&
    job?.transcriptUnlockType !== 'ASONE_ENHANCED' &&
    organization?.billingAccount?.billingConfiguration?.enhancedOnDemandRateType &&
    organization?.billingAccount?.billingConfiguration?.enhancedOnDemandRateType !== 'DISALLOWED' &&
    job?.status === 'JOB_STATUS_READY_FOR_TRANSCRIBING';

  const showActions =
    canRegenerateWithLayout ||
    showClaimButton(job, organization) ||
    showMarkAsCompleteButton(job, organization) ||
    showMarkAsEditedButton(job, organization) ||
    showMarkAsProofreadButton(job, organization) ||
    canTriggerEnhanced;

  const showLinkToUser = !!organization?.permissions?.manageMembers;

  const canReassignJob = job?.status && organization?.permissions?.reassignJobs && jobCanBeReassigned(job.status);

  useEffect(() => {
    if (!workspaceId || !updatedJob || updatedJob.id !== jobId) {
      return;
    }
    queryClient.setQueryData<JobResponseDto>(jobsKeys.workspaceDetail(jobId, workspaceId), (currentJob) =>
      currentJob ? { ...currentJob, ...updatedJob } : updatedJob,
    );
  }, [jobId, workspaceId, updatedJob, queryClient]);

  useEffect(() => {
    if (!jobAccessLostId || !jobId) {
      return;
    }
    if (jobAccessLostId === jobId) {
      toast.error('You have lost access to this job');
      navigate(routes.jobsList.make(organization?.slug, wsSlug));
    }
  }, [jobAccessLostId, jobId, wsSlug, navigate]);

  useEffect(() => {
    setErrorJobNameMessage('');

    if ((jobName || '').startsWith(' ')) {
      setErrorJobNameMessage(EErrorMessages.JOB_NAME_STARTS_WITH_SPACE);
      return;
    }

    if ((jobName || '').endsWith(' ')) {
      setErrorJobNameMessage(EErrorMessages.JOB_NAME_ENDS_WITH_SPACE);
      return;
    }

    if (sanitize(jobName || '') !== (jobName || '')) {
      setErrorJobNameMessage(EErrorMessages.WRONG_JOB_NAME_SYMBOLS);
      return;
    }

    if (hasSpecialCharacters(jobName || '')) {
      setErrorJobNameMessage(EErrorMessages.WRONG_JOB_NAME_SYMBOLS);
      return;
    }

    if ((jobName || '').length > 255) {
      setErrorJobNameMessage(EErrorMessages.JOB_NAME_IS_TOO_LONG);
    }
  }, [jobName]);

  const handleDeleteAttachment = (file: { path: string; fileName: string }) => {
    if (!organization?.id) {
      toast.error('Organization ID not found');
      return;
    }
    if (job?.id) {
      deleteAttachmentMutation.mutate(file.path);
    }
  };

  const renderContactAdministratorContent = () => {
    return (
      <>
        <Helmet>
          <title>Job Details - AutoScript</title>
        </Helmet>
        <Header title="Job Details" />

        <NoAccessWrapper>
          <NoAccessTitle>Job not found</NoAccessTitle>
          <NoAccessContent>
            If you believe you should have access to this job please contact your Organization administrator
          </NoAccessContent>
        </NoAccessWrapper>
      </>
    );
  };

  const renderJobIsDeletedContent = () => {
    return (
      <>
        <Helmet>
          <title>Job Details - AutoScript</title>
        </Helmet>
        <Header title="Job Details" />

        <NoAccessWrapper>
          <NoAccessTitle>Job is deleted</NoAccessTitle>
          <NoAccessContent>Something went wrong</NoAccessContent>
        </NoAccessWrapper>
      </>
    );
  };

  const renderJobNotFound = () => {
    return (
      <>
        <Helmet>
          <title>Job Details - AutoScript</title>
        </Helmet>
        <Header title="Job Details" />

        <NoAccessWrapper>
          <NoAccessTitle>Job not found</NoAccessTitle>
          <NoAccessContent>Please contact your Organization Administrator</NoAccessContent>
        </NoAccessWrapper>
      </>
    );
  };

  if (organization && workspace && !isWorkspaceAccessible) {
    return renderContactAdministratorContent();
  }

  if (job?.removed) {
    return renderJobIsDeletedContent();
  }

  if (!job && jobQuery.isError) {
    return renderJobNotFound();
  }

  const saveJobTitle = () => {
    if (!jobName?.length || jobName === 'Unnamed' || errorJobNameMessage) {
      return;
    }
    saveTitleMutation.mutate(jobName);
  };

  const renderOpenInASOne = () => {
    const getASOneLinkPrefix = () => {
      return `${window.location.origin}/as-one/job/`;
    };
    const showButton = job?.status && !job.asOneLocked && jobIsScheduled(job.status);
    if (!showButton) {
      return null;
    }
    return (
      <StyledIconLink target="_blank" rel="noreferrer" href={`${getASOneLinkPrefix()}${job?.meetingId}`}>
        <ASOneIcon />
        Open with AutoScriptOne
      </StyledIconLink>
    );
  };

  const renderShareLink = () => {
    const handleCopyButtonClick = (link: string) => {
      navigator.clipboard.writeText(link);
    };

    const showShareButton = job?.status ? jobCanBeShared(job.status) : false;
    if (!showShareButton) {
      return null;
    }
    return (
      <ToolbarActionButton
        title="Copy link to clipboard"
        onClick={() => {
          toast.success('The link has been copied to the clipboard', {
            duration: 2000,
          });
          handleCopyButtonClick(`${getLinkPrefix()}${job?.id}`);
        }}
      >
        <Share />
        Share job link
      </ToolbarActionButton>
    );
  };

  const renderWatchButton = () => {
    const showWatchButton = !!job;
    if (!showWatchButton) {
      return null;
    }
    if (meWatcher) {
      return (
        <ToolbarActionButton
          title="Unwatch this job"
          onClick={async (e) => {
            setAnchorEl(e.target);
          }}
        >
          <EyeClosedIcon style={{ marginRight: '-5px', height: '28px', width: '28px' }} />
          Unwatch
        </ToolbarActionButton>
      );
    }
    return (
      <ToolbarActionButton
        title="Watch this job"
        onClick={async (e) => {
          setAnchorEl(e.target);
        }}
      >
        <EyeIcon style={{ marginRight: '-5px', height: '28px', width: '28px' }} />
        Watch
      </ToolbarActionButton>
    );
  };

  const renderWatchSettings = () => {
    return (
      <Popover
        open={anchorEl !== null}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorEl={anchorEl as HTMLElement}
        panelClassName="right-0 top-[30px] overflow-hidden rounded-[5px] px-[15px] py-2.5 shadow-[0_10px_15px_rgba(0,0,0,0.1)]"
      >
        <div className="popover-list flex flex-col gap-1">
          <div className="input-wrapper checkbox-wrapper flex gap-1">
            <CheckBox
              name="watch-status"
              disabled={watchMutation.isPending}
              checked={watchStatus}
              onChange={(v) => updateWatchSettings({ status: v, assignee: watchAssignee, comments: watchComments })}
            />
            <label>Status updates</label>
          </div>
          <Divider className="divider" />
          <div className="input-wrapper checkbox-wrapper flex gap-1">
            <CheckBox
              name="watch-assignee"
              disabled={watchMutation.isPending}
              checked={watchAssignee}
              onChange={(v) => updateWatchSettings({ status: watchStatus, assignee: v, comments: watchComments })}
            />
            <label>Assignee updates</label>
          </div>
          <Divider className="divider" />
          <div className="input-wrapper checkbox-wrapper flex gap-1">
            <CheckBox
              name="watch-comments"
              disabled={watchMutation.isPending}
              checked={watchComments}
              onChange={(v) => updateWatchSettings({ status: watchStatus, assignee: watchAssignee, comments: v })}
            />
            <label>Comments</label>
          </div>
        </div>
      </Popover>
    );
  };

  const editAssignee = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.AssignJobModal, {
      job,
      onSuccess: () => {
        closeModal();
        reload();
      },
    });
  };

  const editTags = () => {
    openModal(ModalNamesEnum.EditJobTagsModal, {
      job,
      onSuccess: () => {
        closeModal();
        reload();
      },
    });
  };

  const editNotes = () => {
    openModal(ModalNamesEnum.EditJobNotesModal, {
      job,
      onSuccess: () => {
        closeModal();
        reload();
      },
    });
  };

  const editCase = () => {
    openModal(ModalNamesEnum.EditJobCaseModal, {
      job,
      onSuccess: () => {
        closeModal();
        reload();
      },
    });
  };

  const triggerEnhanced = () => {
    openModal(ModalNamesEnum.PriceModal, {
      onCancel: onModalCancel,
      setPaymentIntent,
      paymentIntent,
      onSuccess: handleEnhancedTrigger,
      jobId,
      enhancedPaymentVariant: true,
      freeTrialInEffect: organization?.orgSubscriptions?.some((subscription) => subscription.status === 'trialing'),
    });
  };

  const onModalCancel = () => {
    closeModal();
  };

  const handleEnhancedTrigger = () => {
    closeModal();
    enhanceMutation.mutate();
  };

  const complicatedTitleEdit = () => {
    if (!organization?.permissions?.editJobs) {
      return job?.name;
    }

    if (editTitle) {
      return (
        <div className="pb-5">
          <div className="flex gap-2.5">
            <Input
              className="min-w-0 flex-1"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setJobName(e.target.value)}
              name="name"
              label="Title"
              value={jobName}
              required={true}
            />
            <IconButtonClean
              title="Save"
              disabled={
                !jobName?.length || jobName === 'Unnamed' || !!errorJobNameMessage || saveTitleMutation.isPending
              }
              onClick={() => saveJobTitle()}
            >
              <DoneIcon className="h-[18px] w-[18px]" />
            </IconButtonClean>
          </div>
          {errorJobNameMessage ? <ErrorMessage error={errorJobNameMessage} className="mt-1 text-left text-sm" /> : null}
        </div>
      );
    }

    return (
      <FlexTitle>
        <FlexTitleText>{job?.name}</FlexTitleText>
        <IconButtonClean
          title="Edit"
          onClick={() => {
            setJobName(job?.name ?? '');
            setEditTitle(true);
          }}
        >
          <PencilIcon className="h-[22px] w-[22px] text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer" />
        </IconButtonClean>
      </FlexTitle>
    );
  };

  const downloadFileVersion = async (jobId: string, versionId?: string | null, jobName = 'job') => {
    const fileName = sanitize(jobName)
      .toLowerCase()
      .replace(/[^a-zA-Z0-9()_.]/gi, '-');
    await downloadFromUrl(
      `${window.location.origin}/api/media/${jobId}/versions/${versionId}`,
      `${fileName}.docx`,
    ).catch(handleDownloadError);
  };

  const downloadFile = (jobId: string, fileName: string) =>
    downloadFromUrl(
      `${window.location.origin}/api/media/${jobId}/transcript/${encodeURIComponent(fileName)}`,
      buildJobMaterialFileName(job?.name || 'job', fileName),
    );

  const downloadSourceFile = async (jobId: string, fileName: string) => {
    const signed = await call(() =>
      MediaService.downloadSignedMediaFile({
        jobId,
        file: fileName,
        disposition: 'attachment',
      }),
    );
    const signedUrl = signed?.signedUrl ?? '';
    if (!signedUrl) {
      throw new Error('No signed URL');
    }
    await downloadFromUrl(signedUrl, buildJobMaterialFileName(job?.name || 'job', fileName));
  };

  const downloadPlaybackAudio = async () => {
    const playbackJobId = job?.id;
    const playbackFile = job?.playbackAudioFilename;
    if (!playbackJobId || !playbackFile) {
      return;
    }
    const signed = await call(() =>
      MediaService.downloadSignedMediaFile({
        jobId: playbackJobId,
        file: playbackFile,
        disposition: 'attachment',
      }),
    );
    const signedUrl = signed?.signedUrl ?? '';
    if (!signedUrl) {
      throw new Error('No signed URL');
    }
    await downloadFromUrl(signedUrl, buildJobMaterialFileName(job?.name || 'job', playbackFile));
  };

  const downloadReporterNotes = (jobId: string, fileName: string) =>
    downloadFromUrl(
      `${window.location.origin}/api/media/${jobId}/reporter-notes/${encodeURIComponent(fileName)}`,
      buildJobMaterialFileName(job?.name || 'job', fileName),
    );

  const downloadExhibitFiles = (jobId: string, fileName: string) =>
    downloadFromUrl(
      `${window.location.origin}/api/media/${jobId}/exhibits/${encodeURIComponent(fileName)}`,
      buildJobMaterialFileName(job?.name || 'job', fileName),
    );

  const downloadAttachment = (jobId: string, fullPath: string, fileName?: string) =>
    downloadFromUrl(
      `${window.location.origin}/api/media/${jobId}/attachments/${encodeURIComponent(fullPath)}`,
      buildJobMaterialFileName(job?.name || 'job', fileName ?? fullPath),
    );

  const openNoticeExtraction = (attachmentId: string) => {
    window.open(
      `${window.location.origin}${routes.noticeExtractor.make(params.orgSlug, params.wsSlug)}?workflowId=${encodeURIComponent(attachmentId)}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  type meetingType = 'RECORDING_SESSION_REMOTE' | 'RECORDING_SESSION_HYBRID' | 'RECORDING_SESSION_IN_PERSON';
  const meetingTypeNames = {
    RECORDING_SESSION_REMOTE: 'Remote',
    RECORDING_SESSION_HYBRID: 'Hybrid',
    RECORDING_SESSION_IN_PERSON: 'In Person',
  };

  const editDeadline = () => {
    openModal(ModalNamesEnum.EditDeadline, {
      onSuccess: () => {
        reload();
        closeModal();
      },
      job,
    });
  };

  const regenerateWithLayout = (job: JobResponseDto) => {
    const wsid = job.workspaceId;

    if (!wsid || !layoutId) {
      return;
    }

    void openCloudLayoutRegenerationFlow({
      openModal,
      closeModal,
      hasVersions: hasVersionsBeyondLayoutDraft(job.layoutId, job.versions?.length ?? 0),
      speakerLabels: shouldShowSpeakerLabelOverrides(job.transcriptUnlockType) ? job.speakerLabels ?? [] : [],
      submit: (speakerLabelOverrides) =>
        regenerateWithLayoutMutation.mutateAsync({ jobId: job.id, workspaceId: wsid, speakerLabelOverrides }),
    });
  };

  const showAttachments = !!showDownloads && job.attachments?.length > 0;

  const showReporterNotes = !!showDownloads && !!job?.reporterNotes?.length;

  return (
    <>
      <Helmet>
        <title>Job Details - AutoScript</title>
      </Helmet>
      <Header title="Job Details" />
      <LinearProgressLoader active={isBusy} />

      {job ? (
        <Wrapper>
          <TopRightSection>
            {renderOpenInASOne()}
            {renderWatchButton()}
            {renderWatchSettings()}
            {organization?.featureFlags?.AS_ONE_BACKUP_RECORDING ? (
              <AttachBackupButton id={jobId} workspaceId={workspaceId} reload={reload} />
            ) : null}
            {renderShareLink()}
          </TopRightSection>
          <JobSection>
            <JobSectionLeft>General</JobSectionLeft>
            <JobSectionRight>
              <JobRightRow>{complicatedTitleEdit()}</JobRightRow>
              <InfoBlockWrapper>
                <InfoBlock>
                  <InfoBlockTitle>Job ID</InfoBlockTitle>
                  <InfoBlockContent>{job.id}</InfoBlockContent>
                </InfoBlock>

                <InfoBlock>
                  <InfoBlockTitle>Status</InfoBlockTitle>
                  <InfoBlockContent id="job_details_status">
                    {JOB_STATUSES_ENUM[job.status as keyof typeof JOB_STATUSES_ENUM]}
                  </InfoBlockContent>
                </InfoBlock>

                {job.bucket ? (
                  <InfoBlock>
                    <InfoBlockTitle>S3 Bucket</InfoBlockTitle>
                    <InfoBlockContent>
                      <a
                        href={`https://s3.console.aws.amazon.com/s3/buckets/${job.bucket}?region=${job.region}&prefix=jobs/${job.id}/`}
                        rel="noreferrer"
                        target="_blank"
                      >
                        Link
                      </a>
                    </InfoBlockContent>
                  </InfoBlock>
                ) : null}

                <InfoBlock>
                  <InfoBlockTitle>Transcript Unlock Type</InfoBlockTitle>
                  <InfoBlockContent className="align-middle flex gap-2">
                    {getTranscriptUnlockTypeName(job.transcriptUnlockType)}
                  </InfoBlockContent>
                </InfoBlock>

                <InfoBlock>
                  <InfoBlockTitle>Date created</InfoBlockTitle>
                  <InfoBlockContent>{getAmericanTime(job.createdAt)}</InfoBlockContent>
                </InfoBlock>

                <InfoBlock>
                  <InfoBlockTitle>Deadline</InfoBlockTitle>
                  <FlexBlockContent>
                    {job.deadline ? formatIsoDateToAmerican(job.deadline) : 'Not set'}
                    {canSubmitJob ? (
                      <PencilIcon
                        className="pencil text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                        onClick={() => editDeadline()}
                      />
                    ) : null}
                  </FlexBlockContent>
                </InfoBlock>

                <InfoBlock>
                  <InfoBlockTitle>Assignee</InfoBlockTitle>
                  <AssigneeBlockContent>
                    {job?.assignee ? (
                      <div className="assignee">
                        <StyledLink to={routes.userDetails.make(organization?.slug ?? '', job?.assignee?.id)}>
                          <p className="assignName">
                            {job?.assignee?.name} {job?.assignee?.lastname}
                          </p>
                          <p className="assignEmail">{job?.assignee?.email || ''}</p>
                        </StyledLink>
                      </div>
                    ) : null}
                    {!job?.assignee ? (
                      <div className="assignee">
                        <p className="assignName">Unassigned</p>
                      </div>
                    ) : null}
                    {canReassignJob ? (
                      <PencilIcon
                        className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                        onClick={() => editAssignee(job)}
                      />
                    ) : null}
                  </AssigneeBlockContent>
                </InfoBlock>

                <InfoBlock>
                  <InfoBlockTitle>Cloud layout</InfoBlockTitle>
                  {isCloudLayoutChoiceAvailable ? (
                    <AutoCompleteSelect
                      name="layoutId"
                      label="Layout"
                      value={layoutId}
                      onChange={(_, value) => setLayoutId(value)}
                      options={layoutsList}
                      required={false}
                      disabled={!layoutsList.length}
                    />
                  ) : (
                    <FlexBlockContent>{job.layoutName || 'None'}</FlexBlockContent>
                  )}
                </InfoBlock>

                {!!job.tags?.length || canEditJob ? (
                  <InfoBlock>
                    <InfoBlockTitle>Tags</InfoBlockTitle>
                    <InfoBlockContent>
                      <Tags>
                        {(job.tags || []).map((tag: string, index: number) => (
                          <TagMuted key={index}>{tag}</TagMuted>
                        ))}
                        {canEditJob ? (
                          <button onClick={() => editTags()}>
                            <PencilIcon className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer" />
                          </button>
                        ) : null}
                      </Tags>
                    </InfoBlockContent>
                  </InfoBlock>
                ) : null}

                {showKeyTerms ? (
                  <InfoBlock>
                    <InfoBlockTitle>Keyterms</InfoBlockTitle>
                    <InfoBlockContent>
                      <KeyTermsList keyTerms={keyTerms} />
                    </InfoBlockContent>
                  </InfoBlock>
                ) : null}
              </InfoBlockWrapper>
            </JobSectionRight>
          </JobSection>

          <Divider />

          {showActions ? (
            <>
              <JobSection>
                <JobSectionLeft>Actions</JobSectionLeft>
                <div className="w-full flex">
                  {canRegenerateWithLayout ? (
                    <div className="w-[200px] mr-4">
                      <OutlinedButton
                        className="flex w-[200px] items-center justify-center gap-2"
                        onClick={() => regenerateWithLayout(job)}
                        disabled={regenerateWithLayoutMutation.isPending}
                      >
                        {regenerateWithLayoutMutation.isPending ? (
                          <LoadingIcon className="h-4 w-4 shrink-0 animate-spin" />
                        ) : null}
                        {regenerateWithLayoutMutation.isPending ? 'Regenerating...' : 'Regenerate with layout'}
                      </OutlinedButton>
                    </div>
                  ) : (
                    <JobStatusActionsButton job={job} reload={reload} />
                  )}
                  {canTriggerEnhanced ? (
                    <div className="w-[200px]">
                      <BoldButton
                        onClick={triggerEnhanced}
                        disabled={enhanceMutation.isPending}
                        className="flex items-center justify-center w-[200px]"
                      >
                        <MagicWand className="mr-2 w-4" />
                        Apply AI-Enhanced
                      </BoldButton>
                    </div>
                  ) : null}
                </div>
              </JobSection>
              <Divider />
            </>
          ) : null}

          {showVersions ? (
            <>
              <JobSection>
                <JobSectionLeft>Versions</JobSectionLeft>
                <div className="w-full">
                  <InfoBlockFullSize>
                    <CheckoutEventsTableWrapper>
                      <TableWrapper>
                        <Table>
                          <Thead>
                            <Tr>
                              <Th>Assignee</Th>
                              <Th>Checkout</Th>
                              <Th>Last update</Th>
                              <Th>Status</Th>
                              <Th>Originating version</Th>
                              <Th></Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {job.checkoutEvents?.map((item: any, index: number) => (
                              <Tr key={item.id ?? index}>
                                <Td>
                                  <CellContent>
                                    {showLinkToUser ? (
                                      <Link
                                        to={routes.userDetails.make(organization?.slug ?? '', item.user?.id ?? '')}
                                        className="underline"
                                      >
                                        {item.user?.name && `${item.user?.name} ${item.user?.lastname}`}
                                      </Link>
                                    ) : (
                                      item.user?.name && `${item.user?.name} ${item.user?.lastname}`
                                    )}
                                  </CellContent>
                                </Td>
                                <Td>
                                  <CellContent>{getAmericanTime(item.createdAt)}</CellContent>
                                </Td>
                                <Td>
                                  <CellContent>
                                    {item.finalVersion ? getAmericanTime(item.finalVersion.createdAt) : '-'}
                                  </CellContent>
                                </Td>
                                <Td>
                                  <CellContent>
                                    {JOB_STATUSES_ENUM[item.jobStatus as keyof typeof JOB_STATUSES_ENUM]}
                                  </CellContent>
                                </Td>
                                <Td>
                                  <CellContent>
                                    <a
                                      onClick={() =>
                                        handleOpenDesktop(
                                          job?.id || '',
                                          item.initialVersion?.predecessorId ? item.predecessor?.id : undefined,
                                        )
                                      }
                                      className="download-version-link underline align-left align-items-left text-sm cursor-pointer"
                                    >
                                      {item.initialVersion?.predecessorId
                                        ? `${item.predecessor?.user.name} ${item.predecessor?.user.lastname} - ${getAmericanTime(
                                            item.predecessor?.createdAt,
                                          )}`
                                        : 'Speech-to-Text version'}
                                    </a>
                                  </CellContent>
                                </Td>
                                <Td>
                                  <CellContent className="flex flex-row">
                                    <StyledButtonIconLink
                                      onClick={() => downloadFileVersion(job?.id || '', item.finalVersionId, job?.name)}
                                      className="download-version-link !w-[30px]"
                                    >
                                      <DownloadJob />
                                    </StyledButtonIconLink>
                                    <StyledDesktopButtonIconLink
                                      className="!w-[30px]"
                                      onClick={() => handleOpenDesktop(job?.id || '', item.finalVersionId)}
                                    >
                                      <DesktopIcon />
                                    </StyledDesktopButtonIconLink>
                                  </CellContent>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableWrapper>
                    </CheckoutEventsTableWrapper>
                  </InfoBlockFullSize>
                </div>
              </JobSection>
              <Divider />
            </>
          ) : canBeOpenedInASD ? (
            <>
              <JobSection>
                <JobSectionLeft>Versions</JobSectionLeft>
                <div className="w-full">
                  <InfoBlockFullSize>
                    <div className="w-[200px]">
                      <BoldButton
                        onClick={(e) => {
                          e.preventDefault();
                          handleOpenDesktop(job?.id || '');
                        }}
                      >
                        Start editing in AutoScript
                      </BoldButton>
                    </div>
                  </InfoBlockFullSize>
                </div>
              </JobSection>
              <Divider />
            </>
          ) : null}

          {!!(job.checkoutEvents || []).length && <MetricsList job={job} />}

          {showMaterials ? (
            <>
              <JobSection>
                <JobSectionLeft>Materials</JobSectionLeft>
                <JobSectionRight className="flex flex-col gap-6">
                  {canUploadMaterials ? (
                    <UploadAttachmentsBlock
                      jobId={job?.id}
                      organizationId={String(organization?.id)}
                      noticeExtractorEnabled={organization?.featureFlags?.NOTICE_EXTRACTOR_ENABLED === true}
                      reload={reload}
                    />
                  ) : null}
                  <InfoBlockFullSize>
                    <InfoBlockTitle>Case</InfoBlockTitle>
                    <InfoBlockContent className="align-middle flex gap-2">
                      {job.repository?.slug ? (
                        <>
                          <Restricted to={EPermission.viewRepositories}>
                            <StyledLink
                              to={{
                                pathname: routes.repositoryDetails.make(
                                  params.orgSlug,
                                  job?.repository?.slug,
                                  job?.repository?.id,
                                ),
                              }}
                              state={{
                                from: routes.jobDetails.make(params.orgSlug, params.wsSlug, params.id),
                              }}
                            >
                              <p className="repository-name">{job?.repository?.name}</p>
                            </StyledLink>
                          </Restricted>
                          {job?.repository?.zipUrl ? (
                            <DownloadZipLink
                              onClick={() => caseZipMutation.mutate()}
                              disabled={caseZipMutation.isPending}
                            >
                              <DownloadZip /> <span>Download ZIP</span>
                            </DownloadZipLink>
                          ) : null}
                        </>
                      ) : (
                        'Not set'
                      )}
                      {canEditJob ? (
                        <button className="pencil-button" onClick={() => editCase()}>
                          <PencilIcon className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer" />
                        </button>
                      ) : null}
                    </InfoBlockContent>
                  </InfoBlockFullSize>

                  {showSummary && job?.status !== 'JOB_STATUS_PROCESSING' && (
                    <InfoBlockFullSize>
                      <InfoBlockTitle className="flex justify-start items-center">
                        Reporter Summary
                        <FileDownloadButton
                          label="Download reporter summary ZIP"
                          showLabel={false}
                          onDownload={() =>
                            downloadViaBrowser(
                              `${window.location.origin}/api/jobs/${job.id}/partial-unlocked-files/download-zip`,
                              `${sanitize(job.name || 'job')}-reporter-summary.zip`,
                            )
                          }
                          onError={handleDownloadError}
                          className="ml-2"
                        />
                      </InfoBlockTitle>
                      <InfoBlockContent>
                        <div className="file-grid">
                          <ExternalFileDownloadLink href={summaryUrl} label="Court_Reporting_Summary.PDF" />
                          <ExternalFileDownloadLink href={outputUrl} label="Court_Reporting_Audio.MP3" />
                        </div>
                      </InfoBlockContent>
                    </InfoBlockFullSize>
                  )}

                  {showDownloads ? (
                    <InfoBlockFullSize>
                      <InfoBlockTitle>Speech-to-Text Transcript</InfoBlockTitle>
                      <InfoBlockContent>
                        <div className="file-grid">
                          {job.results?.map((file) => (
                            <FileDownloadButton
                              testId={`jobDownloadItem${job.name}`}
                              key={file}
                              label={file}
                              onDownload={() => downloadFile(job.id, file)}
                              onError={handleDownloadError}
                              className="downloadLink"
                            />
                          ))}
                        </div>
                      </InfoBlockContent>
                    </InfoBlockFullSize>
                  ) : null}

                  {showAttachments ? (
                    <InfoBlockFullSize>
                      <InfoBlockTitle className="flex items-center">
                        Attachments
                        <FileDownloadButton
                          label="Download attachments ZIP"
                          showLabel={false}
                          onDownload={() =>
                            downloadViaBrowser(
                              `${window.location.origin}/api/jobs/${job.id}/attachments/download-zip`,
                              `${sanitize(job.name || 'job')}-attachments.zip`,
                            )
                          }
                          onError={handleDownloadError}
                          className="ml-2"
                        />
                      </InfoBlockTitle>
                      <InfoBlockContent>
                        <div className="file-grid">
                          {job.attachments?.map((file: TJobAttachmentWithNoticeData) => (
                            <div key={file.path} className="file-grid__item">
                              <FileDownloadButton
                                testId={`jobDownloadItem${job.name}`}
                                label={file.fileName}
                                onDownload={() => downloadAttachment(job.id, file.path, file.fileName)}
                                onError={handleDownloadError}
                                className="downloadLink"
                              />
                              {organization?.featureFlags?.NOTICE_EXTRACTOR_ENABLED === true &&
                              isNoticePdfAttachment(file) ? (
                                <>
                                  {file.extracted ? (
                                    <IconButtonRegular
                                      aria-label={`Open notice extraction for ${file.fileName}`}
                                      title="Open notice extraction"
                                      onClick={() => openNoticeExtraction(file.id as string)}
                                    >
                                      <DiagonalArrowIcon className="w-4 h-4" />
                                    </IconButtonRegular>
                                  ) : null}
                                  {!file.extracted ? (
                                    <IconButtonRegular
                                      aria-label={`Notice extraction running for ${file.fileName}`}
                                      title="Extracting notice"
                                      disabled
                                    >
                                      <LoadingIcon className="h-4 w-4 animate-spin" />
                                    </IconButtonRegular>
                                  ) : null}
                                </>
                              ) : null}
                              {canEditJob ? (
                                <button
                                  title="Delete attachment"
                                  disabled={deleteAttachmentMutation.isPending}
                                  onClick={() => handleDeleteAttachment(file)}
                                >
                                  <TrashBin />
                                </button>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </InfoBlockContent>
                    </InfoBlockFullSize>
                  ) : null}

                  {showSourceFiles ? (
                    <InfoBlockFullSize>
                      <InfoBlockTitle>Source Files</InfoBlockTitle>
                      <InfoBlockContent className="max-h-96 overflow-y-auto">
                        <div className="file-grid">
                          {job.sourceFiles?.map((file) => (
                            <FileDownloadButton
                              testId={`jobDownloadItem${job.name}`}
                              key={file}
                              label={file}
                              onDownload={() => downloadSourceFile(job.id, file)}
                              onError={handleDownloadError}
                              className="downloadLink"
                            />
                          ))}
                        </div>
                      </InfoBlockContent>
                    </InfoBlockFullSize>
                  ) : null}

                  {showBackupFiles ? (
                    <InfoBlockFullSize>
                      <InfoBlockTitle>Backup Files</InfoBlockTitle>
                      <InfoBlockContent>
                        <div className="file-grid">
                          {job.backupFiles?.map((file) => (
                            <AudioPlayer
                              key={file}
                              src={`${window.location.origin}/api/media/${jobId}/backup/${file}`}
                              downloadFileName={buildJobMaterialFileName(job.name, file)}
                            />
                          ))}
                        </div>
                      </InfoBlockContent>
                    </InfoBlockFullSize>
                  ) : null}

                  {job.exhibitFiles?.length ? (
                    <InfoBlockFullSize>
                      <InfoBlockTitle>Exhibit Files</InfoBlockTitle>
                      <InfoBlockContent>
                        <div className="file-grid">
                          {job.exhibitFiles?.map((file) => (
                            <FileDownloadButton
                              testId={`jobDownloadItem${job.name}`}
                              key={file}
                              label={file}
                              onDownload={() => downloadExhibitFiles(job.id, file)}
                              onError={handleDownloadError}
                              className="downloadLink"
                            />
                          ))}
                        </div>
                      </InfoBlockContent>
                    </InfoBlockFullSize>
                  ) : null}

                  {showReporterNotes ? (
                    <InfoBlockFullSize>
                      <InfoBlockTitle>Reporter notes</InfoBlockTitle>
                      <InfoBlockContent>
                        <div className="file-grid">
                          {job.reporterNotes?.map((file) => (
                            <FileDownloadButton
                              testId={`jobDownloadItem${job.name}`}
                              key={file}
                              label={file}
                              onDownload={() => downloadReporterNotes(job.id, file)}
                              onError={handleDownloadError}
                              className="downloadLink"
                            />
                          ))}
                        </div>
                      </InfoBlockContent>
                    </InfoBlockFullSize>
                  ) : null}

                  {job.playbackAudioFilename ? (
                    <InfoBlockFullSize>
                      <InfoBlockTitle>
                        <FlexNotes>Audio</FlexNotes>
                      </InfoBlockTitle>
                      <InfoBlockContent>
                        {playbackAudioLoading ? (
                          <LinearProgressLoader active />
                        ) : playbackAudioUrl ? (
                          <AudioPlayer
                            src={playbackAudioUrl}
                            downloadFileName={buildJobMaterialFileName(job.name, job.playbackAudioFilename)}
                            onDownload={downloadPlaybackAudio}
                          />
                        ) : (
                          'Audio unavailable'
                        )}
                      </InfoBlockContent>
                    </InfoBlockFullSize>
                  ) : null}

                  {!!job.notes || canEditJob ? (
                    <InfoBlockFullSize>
                      <InfoBlockTitle>
                        <FlexNotes>
                          Notes
                          {canEditJob ? (
                            <button onClick={() => editNotes()}>
                              <PencilIcon className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer" />
                            </button>
                          ) : null}
                        </FlexNotes>
                      </InfoBlockTitle>
                      <InfoBlockContent>{job.notes?.length ? job.notes : 'Empty'}</InfoBlockContent>
                    </InfoBlockFullSize>
                  ) : null}
                </JobSectionRight>
              </JobSection>
              <Divider />
            </>
          ) : null}

          <JobSection>
            <JobSectionLeft>Comments</JobSectionLeft>
            <div className="w-full">
              <CommentForm
                workspaceId={workspaceId}
                jobId={jobId}
                onSuccess={() => {
                  if (websocketsOffline) {
                    reload();
                  }
                }}
              />
              <CommentsList comments={job.comments} />
            </div>
          </JobSection>
          <Divider />

          {showSchedule ? (
            <>
              <JobSection>
                <JobSectionLeft>Schedule</JobSectionLeft>
                <JobSectionRight>
                  <InfoBlockWrapper>
                    <InfoBlock>
                      <InfoBlockTitle>Date and time</InfoBlockTitle>
                      <InfoBlockContent>
                        {job.meetingDate && job.meetingTime ? (
                          <>
                            {getAmericanTime(job.meetingDate ?? '', false)} {job.meetingTime}
                          </>
                        ) : (
                          'Not Set'
                        )}
                      </InfoBlockContent>
                    </InfoBlock>

                    {job.meetingAssigneeId ? (
                      <InfoBlock>
                        <InfoBlockTitle>Court reporter assignee</InfoBlockTitle>
                        <InfoBlockContent>
                          <div className="assignee">
                            <StyledLink
                              to={routes.userDetails.make(organization?.slug ?? '', job?.meetingAssignee?.id ?? '')}
                            >
                              <p className="assignName">
                                {job?.meetingAssignee?.name} {job?.meetingAssignee?.lastname}
                              </p>
                              <p className="assignEmail">{job?.meetingAssignee?.email || ''}</p>
                            </StyledLink>
                          </div>
                        </InfoBlockContent>
                      </InfoBlock>
                    ) : null}

                    <InfoBlock>
                      <InfoBlockTitle>Meeting type</InfoBlockTitle>
                      <InfoBlockContent>
                        {meetingTypeNames[(job.meetingType as meetingType) ?? 'RECORDING_SESSION_REMOTE']}
                      </InfoBlockContent>
                    </InfoBlock>

                    {job.meetingType !== 'RECORDING_SESSION_REMOTE' ? (
                      <InfoBlock>
                        <InfoBlockTitle>Address</InfoBlockTitle>
                        <InfoBlockContent>
                          {job.meetingAddress?.length ? job.meetingAddress : 'Not set'}
                        </InfoBlockContent>
                      </InfoBlock>
                    ) : null}

                    {job.meetingType !== 'RECORDING_SESSION_IN_PERSON' && job.meetingLink?.length ? (
                      <InfoBlock>
                        <InfoBlockTitle>Meeting link</InfoBlockTitle>
                        <InfoBlockContent>
                          <a target="_blank" href={job.meetingLink ?? ''} rel="noreferrer">
                            Meeting link to start session
                          </a>
                        </InfoBlockContent>
                      </InfoBlock>
                    ) : null}
                  </InfoBlockWrapper>
                </JobSectionRight>
              </JobSection>
              <Divider />
            </>
          ) : null}

          {showSchedule && job.meetingId && job.attorneyAccesses?.length ? (
            <>
              <JobSection>
                <JobSectionLeft>Attorney Accesses</JobSectionLeft>
                <JobSectionRight className="w-full">
                  <InfoBlockWrapper className="w-full">
                    <AttorneysBlock attorneyAccesses={job.attorneyAccesses} />
                  </InfoBlockWrapper>
                </JobSectionRight>
              </JobSection>
              <Divider />
            </>
          ) : null}

          {job.assigneeHistory?.length ? (
            <>
              <JobSection>
                <JobSectionLeft>Workflow</JobSectionLeft>
                <div className="w-full">
                  <InfoBlockFullSize>
                    <AssigneeTableWrapper>
                      <TableWrapper>
                        <Table>
                          <Thead>
                            <Tr>
                              <Th style={{ width: '200px' }}>Change date</Th>
                              <Th>User assigned</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {job.assigneeHistory?.map((item: any, index: number) => (
                              <Tr key={item.id ?? index}>
                                <Td>
                                  <CellContent>{getAmericanTime(item.createdAt)}</CellContent>
                                </Td>
                                <Td>
                                  <CellContent>
                                    {item.user?.name && `${item.user?.name} ${item.user?.lastname}`}
                                    {!item.user?.name && 'Unassigned'}
                                  </CellContent>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableWrapper>
                    </AssigneeTableWrapper>
                  </InfoBlockFullSize>
                </div>
              </JobSection>
              <Divider />
            </>
          ) : null}
        </Wrapper>
      ) : null}
    </>
  );
};
